import { el, clear } from "../utils/dom.ts";
import { getIndicatorsWeekly, type WeeklyIndicator } from "../services/api-client.ts";
import { DOMAINS, DOMAIN_LABELS, type Domain } from "../../functions/_shared/event-taxonomy.ts";
import { signalSourceLabel } from "./signal-source-label.ts";
import { goToProvince } from "../app/tab-router.ts";

// Heatmap: province (rows) x ISO week (cols) for one domain. Colour by event_count
// or severity_sum (toggle). Scrolls horizontally on mobile. CSS grid of div cells
// (no chart lib needed). Given ~2% province coverage at launch, most cells are the
// "no data" state — that's expected and shown as an empty grey cell.
export class EventsHeatmapPanel {
  readonly el: HTMLElement;
  private body: HTMLElement;
  private domain: Domain = "policy";
  private metric: "event_count" | "severity_sum" = "event_count";
  private provinceNames: Map<string, string>;

  constructor(provinceNames: Map<string, string> = new Map()) {
    this.provinceNames = provinceNames;
    this.body = el("div", { className: "heatmap-body" }, ["Đang tải…"]);

    const domainSel = el(
      "select",
      { className: "heatmap-domain", onchange: (e: Event) => this.onDomain(e) },
      DOMAINS.map((d) => el("option", { value: d }, [DOMAIN_LABELS[d]])),
    ) as HTMLSelectElement;
    domainSel.value = this.domain;

    const metricSel = el(
      "select",
      { className: "heatmap-metric", onchange: (e: Event) => this.onMetric(e) },
      [
        el("option", { value: "event_count" }, ["Số sự kiện"]),
        el("option", { value: "severity_sum" }, ["Mức độ"]),
      ],
    ) as HTMLSelectElement;

    this.el = el("section", { className: "panel heatmap-panel" }, [
      el("div", { className: "panel-head" }, [
        el("h2", {}, ["Bản đồ nhiệt sự kiện theo tỉnh × tuần"]),
        signalSourceLabel("media"),
      ]),
      el("div", { className: "heatmap-controls" }, [domainSel, metricSel]),
      this.body,
    ]);
  }

  private onDomain(e: Event): void {
    this.domain = (e.target as HTMLSelectElement).value as Domain;
    void this.load();
  }
  private onMetric(e: Event): void {
    this.metric = (e.target as HTMLSelectElement).value as "event_count" | "severity_sum";
    void this.load();
  }

  async load(): Promise<void> {
    try {
      const { items } = await getIndicatorsWeekly(`?domain=${this.domain}&weeks=12`);
      this.render(items);
    } catch {
      clear(this.body);
      this.body.append(el("p", { className: "muted" }, ["Không tải được dữ liệu."]));
    }
  }

  private render(rows: WeeklyIndicator[]): void {
    clear(this.body);
    // Exclude the national ('') row from the province grid.
    const provRows = rows.filter((r) => r.province_code !== "");
    if (provRows.length === 0) {
      this.body.append(
        el("div", { className: "heatmap-empty muted" }, [
          "Chưa có sự kiện gắn tỉnh cho lĩnh vực này — dữ liệu cấp tỉnh đang tích lũy. ",
          "Xem tổng hợp cấp quốc gia ở tab Tổng quan.",
        ]),
      );
      return;
    }

    const weeks = [...new Set(rows.map((r) => r.week))].sort().slice(-12);
    const provinces = [...new Set(provRows.map((r) => r.province_code))];
    const cellVal = new Map<string, number>();
    let max = 1;
    for (const r of provRows) {
      const v = this.metric === "event_count" ? r.event_count : r.severity_sum;
      cellVal.set(`${r.province_code}|${r.week}`, v);
      if (v > max) max = v;
    }

    const grid = el("div", {
      className: "heatmap-grid",
      style: `grid-template-columns: minmax(90px, auto) repeat(${weeks.length}, 1fr)`,
    });
    grid.append(el("div", { className: "heatmap-corner" }, [""]));
    for (const w of weeks) grid.append(el("div", { className: "heatmap-col-head" }, [w.replace(/^\d{4}-/, "")]));

    for (const p of provinces) {
      const name = this.provinceNames.get(p) || p;
      grid.append(
        el("div", {
          className: "heatmap-row-head heatmap-row-link",
          title: `Xem ${name}`,
          onclick: () => goToProvince(p),
        }, [name]),
      );
      for (const w of weeks) {
        const v = cellVal.get(`${p}|${w}`) ?? 0;
        const intensity = v === 0 ? 0 : 0.15 + 0.85 * (v / max);
        grid.append(
          el("div", {
            className: "heatmap-cell",
            title: `${name} · ${w}: ${v}`,
            style: v === 0 ? "" : `background: rgba(37,99,235,${intensity.toFixed(2)})`,
          }),
        );
      }
    }
    this.body.append(el("div", { className: "heatmap-scroll" }, [grid]));
  }
}
